import { useEffect, useState } from 'react'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Pagination } from '@/components/ui'
import apiClient from '@/api/client'
import type { TaskForce } from '@/types'

export default function AdminTaskForcePage() {
  const [taskforces, setTaskforces] = useState<TaskForce[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [taskforceToDelete, setTaskforceToDelete] = useState<TaskForce | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Create Modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    deadline: '',
  })

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState('')
  const [taskforceToEdit, setTaskforceToEdit] = useState<TaskForce | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: 'planning',
    deadline: '',
  })

  useEffect(() => {
    fetchTaskforces()
  }, [currentPage, searchQuery, statusFilter])

  const fetchTaskforces = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/taskforces/', {
        params: {
          page: currentPage,
          search: searchQuery || undefined,
          status: statusFilter || undefined,
        },
      })
      setTaskforces(response.data.results || response.data)
      setTotalPages(Math.ceil((response.data.count || response.data.length) / 20))
      setTotalItems(response.data.count || response.data.length)
    } catch (error) {
      console.error('Error fetching taskforces:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!taskforceToDelete) return
    setDeleting(true)
    try {
      await apiClient.delete(`/admin/taskforces/${taskforceToDelete.id}/`)
      setShowDeleteModal(false)
      setTaskforceToDelete(null)
      fetchTaskforces()
    } catch (error) {
      console.error('Error deleting taskforce:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleOpenEdit = (tf: TaskForce) => {
    setTaskforceToEdit(tf)
    setEditForm({
      name: tf.name,
      description: tf.description || '',
      status: tf.status,
      deadline: tf.deadline ? tf.deadline.split('T')[0] : '',
    })
    setEditError('')
    setShowEditModal(true)
  }

  const handleEdit = async () => {
    if (!taskforceToEdit) return
    setEditing(true)
    setEditError('')
    try {
      const payload = {
        ...editForm,
        deadline: editForm.deadline || null,
      }
      await apiClient.patch(`/admin/taskforces/${taskforceToEdit.id}/`, payload)
      setShowEditModal(false)
      setTaskforceToEdit(null)
      fetchTaskforces()
    } catch (error: any) {
      console.error('Error updating taskforce:', error)
      if (error.response?.data) {
        const data = error.response.data
        if (typeof data === 'object') {
          const messages = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ')
          setEditError(messages)
        } else {
          setEditError(String(data))
        }
      } else {
        setEditError('Errore durante l\'aggiornamento')
      }
    } finally {
      setEditing(false)
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    try {
      const payload = {
        ...createForm,
        deadline: createForm.deadline || null,
      }
      await apiClient.post('/admin/taskforces/', payload)
      setShowCreateModal(false)
      setCreateForm({
        name: '',
        description: '',
        deadline: '',
      })
      fetchTaskforces()
    } catch (error) {
      console.error('Error creating taskforce:', error)
    } finally {
      setCreating(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const config: Record<string, { variant: 'default' | 'info' | 'warning' | 'success' | 'danger'; label: string }> = {
      planning: { variant: 'warning', label: 'Pianificazione' },
      active: { variant: 'success', label: 'Attivo' },
      completed: { variant: 'info', label: 'Completato' },
      archived: { variant: 'default', label: 'Archiviato' },
    }
    return config[status] || { variant: 'default', label: status }
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento task force..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Task Force</h1>
          <p className="text-gray-600">{totalItems} task force</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova Task Force
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px] relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca task force..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="planning">Pianificazione</option>
            <option value="active">Attivo</option>
            <option value="completed">Completato</option>
            <option value="archived">Archiviato</option>
          </select>
        </div>
      </Card>

      {/* Task Force Table */}
      {taskforces.length > 0 ? (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leader</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membri</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {taskforces.map((tf) => {
                    const statusConfig = getStatusBadge(tf.status)
                    return (
                      <tr key={tf.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{tf.name}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">{tf.description}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {tf.leader_name}
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {tf.members_count}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {tf.open_tickets_count} / {tf.tickets_count}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenEdit(tf)}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              title="Modifica"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => { setTaskforceToDelete(tf); setShowDeleteModal(true); }}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Elimina"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title="Nessuna task force"
            description="Non ci sono task force da visualizzare"
          />
        </Card>
      )}

      {/* Delete Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Task Force">
        <ModalBody>
          {taskforceToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare la task force{' '}
                <span className="font-semibold">{taskforceToDelete.name}</span>?
              </p>
              <p className="text-sm text-red-600">
                Questa azione è irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nuova Task Force">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome task force"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione della task force"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scadenza</label>
              <input
                type="date"
                value={createForm.deadline}
                onChange={(e) => setCreateForm({ ...createForm, deadline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Annulla</Button>
          <Button onClick={handleCreate} disabled={creating || !createForm.name}>
            {creating ? 'Creazione...' : 'Crea Task Force'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditError(''); }} title="Modifica Task Force">
        <ModalBody>
          <div className="space-y-4">
            {editError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {editError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome task force"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione della task force"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="planning">Pianificazione</option>
                <option value="active">Attivo</option>
                <option value="completed">Completato</option>
                <option value="archived">Archiviato</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scadenza</label>
              <input
                type="date"
                value={editForm.deadline}
                onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => { setShowEditModal(false); setEditError(''); }}>Annulla</Button>
          <Button onClick={handleEdit} disabled={editing || !editForm.name}>
            {editing ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
